import { ASSET_TYPES, assetTypeDefinition } from "../../utils/projectAssets";
// src/pages/project/ProjectOverview.jsx
//
// The project home. A project is the portfolio: it owns several assets
// (sites), each of which is studied on its own and, once commissioned, traded
// together with the others in Operations. This page is where assets are
// added, named, moved along their lifecycle and opened for setup or study.

import ProjectReportLogo from "../../components/ProjectReportLogo";
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { useDemo } from "../../context/DemoContext";
import { getDemoMarketOperationsDisabledMessage, getDemoWindSystemDisabledMessage, getMultipleAssetsDisabledMessage } from "../../utils/demoAccess";
import { useFile } from "../../context/FileContext";
import { useLanguage } from "../../context/LanguageContext";
import { ASSET_STATUSES, ASSET_STATUS_META, OPERATING_STATUSES, assetDisplayName, assetStatusMeta, assetSummary, portfolioTotals } from "../../utils/portfolio";

const fmt = (v, d = 1) => (v == null || Number.isNaN(Number(v)) ? "—" : Number(v).toLocaleString("en-GB", { maximumFractionDigits: d, minimumFractionDigits: 0 }));

/**
 * A power or energy figure at the scale it is read at: a 12 kWp rooftop is
 * "12 kWp", not "0.01 MWp". Below 1 MW (1 MWh) the figure is shown in k-units;
 * from 1 MW up it stays in M-units as before.
 * `kilo` is the figure in kW / kWh / kWp; `suffix` is "p" for peak power.
 */
export function scaledFigure(kilo, base = "W", suffix = "") {
  const value = Number(kilo);
  if (kilo == null || !Number.isFinite(value)) return { value: "—", unit: `M${base}${suffix}` };
  if (Math.abs(value) < 1000) {
    return { value: fmt(value, Math.abs(value) < 100 ? 2 : 1), unit: `k${base}${suffix}` };
  }
  return { value: fmt(value / 1000, 2), unit: `M${base}${suffix}` };
}

/**
 * One headline number. The four of them are peers, so none of them is a hero
 * figure; the size difference does the ranking inside a tile instead, between
 * the number and its unit.
 *
 * `unit` is separated from `value` on purpose: "1.06 MWp" set at one size makes
 * the reader work to find the figure. `meter` is for a value that is a share of
 * a whole - a bar says "none of them yet" faster than "0 / 1" does.
 *
 * Figures stay proportional rather than tabular: these are single numbers, not
 * a column to be scanned down.
 */
/**
 * Four accent hues, one per tile, validated together as a categorical palette
 * (lightness band, chroma floor, colour-vision separation and contrast against
 * the surface) for both modes. Colour is carried by a rule beside the label,
 * never by the figure itself: the label is what says what the number means, so
 * nothing is lost to a reader who cannot separate these hues.
 */
export const TILE_ACCENTS = {
  assets: { light: "#6A5ACD", dark: "#7E71D2" },
  pv: { light: "#C2760C", dark: "#BC821F" },
  storage: { light: "#2E9E8F", dark: "#2C9E90" },
  market: { light: "#0B4DA2", dark: "#4886D6" },
};

function Tile({ label, value, unit, hint, meter, accent, muted, mutedNote }) {
  const share =
    meter && Number(meter.max) > 0
      ? Math.max(0, Math.min(100, (Number(meter.value) / Number(meter.max)) * 100))
      : null;
  const hue = (theme) =>
    accent ? TILE_ACCENTS[accent][theme.palette.mode === "dark" ? "dark" : "light"] : "transparent";
  return (
    <Paper
      variant="outlined"
      sx={{
        px: 2,
        py: 1.75,
        borderRadius: 2,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
        // A tile whose subject is switched off stops competing for attention,
        // but stays readable -- the figure is still true, it just cannot change.
        opacity: muted ? 0.55 : 1,
        backgroundColor: muted ? "action.hover" : "background.paper",
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        {accent ? (
          <Box
            sx={{
              width: 3,
              height: 14,
              borderRadius: 2,
              flexShrink: 0,
              backgroundColor: muted ? "text.disabled" : hue,
            }}
          />
        ) : null}
        <Typography
          variant="body2"
          sx={{ color: "text.secondary", fontWeight: 600, lineHeight: 1.25 }}
        >
          {label}
        </Typography>
      </Stack>
      <Stack direction="row" alignItems="baseline" spacing={0.75} sx={{ flexWrap: "wrap", rowGap: 0 }}>
        <Typography
          component="span"
          sx={{ fontSize: 30, fontWeight: 700, lineHeight: 1.15, letterSpacing: "-0.015em" }}
        >
          {value}
        </Typography>
        {unit ? (
          <Typography
            component="span"
            sx={{ fontSize: 14, fontWeight: 600, color: "text.secondary", lineHeight: 1.2 }}
          >
            {unit}
          </Typography>
        ) : null}
      </Stack>
      {share !== null ? (
        <LinearProgress
          variant="determinate"
          value={share}
          sx={{
            height: 4,
            borderRadius: 2,
            mt: 0.5,
            backgroundColor: "action.selected",
            "& .MuiLinearProgress-bar": {
              borderRadius: 2,
              backgroundColor: muted ? "text.disabled" : hue,
            },
          }}
        />
      ) : null}
      {muted && mutedNote ? (
        <Typography variant="caption" sx={{ color: "text.secondary", lineHeight: 1.4, mt: "auto", pt: 0.5, fontStyle: "italic" }}>
          {mutedNote}
        </Typography>
      ) : hint ? (
        <Typography variant="caption" sx={{ color: "text.secondary", lineHeight: 1.4, mt: "auto", pt: 0.5 }}>
          {hint}
        </Typography>
      ) : null}
    </Paper>
  );
}

function StatusChip({ status, onChange, operationsDisabled, isGreek }) {
  const meta = assetStatusMeta(status);
  if (!onChange) return <Chip size="small" label={meta.label} color={meta.color} sx={{ fontWeight: 700 }} />;
  return (
    <Select
      size="small"
      value={ASSET_STATUSES.includes(status) ? status : ""}
      displayEmpty
      inputProps={{ "aria-label": isGreek ? "Κατάσταση μονάδας" : "Asset lifecycle status" }}
      onChange={(e) => { if (e.target.value !== "operating" || !operationsDisabled) onChange(e.target.value); }}
      variant="standard"
      disableUnderline
      renderValue={(v) => <Chip size="small" label={assetStatusMeta(v).label} color={assetStatusMeta(v).color} sx={{ fontWeight: 700, cursor: "pointer" }} />}
      sx={{ "& .MuiSelect-select": { p: 0, pr: "20px !important", display: "flex", alignItems: "center" } }}
    >
      {!ASSET_STATUSES.includes(status) && <MenuItem value="" disabled>Needs review</MenuItem>}
      {ASSET_STATUSES.map((s) => (
        <MenuItem key={s} value={s} disabled={s === "operating" && operationsDisabled}>
          <Stack>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {ASSET_STATUS_META[s].label}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {s === "operating" && operationsDisabled ? (isGreek ? "Απαιτείται άδεια Market Operations." : "Requires Market Operations access.") : ASSET_STATUS_META[s].hint}
            </Typography>
          </Stack>
        </MenuItem>
      ))}
    </Select>
  );
}

export default function ProjectOverview() {
  const file = useFile();
  const { demoProfile, demoError } = useDemo();
  const operationsDisabled = !demoProfile || Boolean(demoError) || Boolean(getDemoMarketOperationsDisabledMessage(demoProfile));
  // Wind is a licensed capability. Keep its asset types visible so the product
  // catalogue stays understandable, but disable them when this account cannot
  // configure or simulate wind.
  const windDisabledMessage = getDemoWindSystemDisabledMessage(demoProfile);
  const multipleAssetsDisabledMessage = getMultipleAssetsDisabledMessage(demoProfile);
  const availableAssetTypes = useMemo(
    () => ASSET_TYPES.filter((t) => !t.hasWind || !windDisabledMessage),
    [windDisabledMessage]
  );
  const { language } = useLanguage();
  const isGreek = language === "el";
  const navigate = useNavigate();
  const { currentProject, portfolio, assets, activeAsset, switchAsset, addAsset, updateAsset, removeAsset, updateProjectMeta } = file;

  const totals = useMemo(() => portfolioTotals(assets), [assets]);
  const operating = assets.filter((a) => OPERATING_STATUSES.includes(a.status));

  const [renameOpen, setRenameOpen] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [renaming, setRenaming] = useState(false);
  const [renameError, setRenameError] = useState("");
  const submitRename = async (event) => {
    event.preventDefault();
    const name = nameDraft.trim();
    if (!name || renaming) return;
    setRenaming(true);
    setRenameError("");
    try {
      await file.rename(name);
      setRenameOpen(false);
    } catch (err) {
      setRenameError(err?.message || "Could not rename the project. Please try again.");
    } finally {
      setRenaming(false);
    }
  };

  const [addDlg, setAddDlg] = useState(false);
  const [addName, setAddName] = useState("");
  const [addFrom, setAddFrom] = useState("");
  const [addType, setAddType] = useState("");
  const [editAsset, setEditAsset] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [error, setError] = useState("");
  const assetAdditionDisabledMessage = assets.length > 0 && multipleAssetsDisabledMessage
    ? (isGreek
      ? "Η προσθήκη πολλών μονάδων στο ίδιο έργο δεν είναι ενεργοποιημένη για αυτόν τον λογαριασμό."
      : multipleAssetsDisabledMessage)
    : "";

  const openSetup = (id) => {
    switchAsset(id);
    navigate("/project/asset?tab=general");
  };
  const openStudy = (id) => {
    switchAsset(id);
    navigate("/feasibility/general");
  };

  const submitAdd = () => {
    if (assetAdditionDisabledMessage) {
      setError(assetAdditionDisabledMessage);
      return;
    }
    if (!availableAssetTypes.some((t) => t.value === addType)) {
      setError(windDisabledMessage || "Choose an asset type before creating the asset.");
      return;
    }
    try {
      addAsset({ name: addName.trim() || `${isGreek ? "Μονάδα" : "Asset"} ${assets.length + 1}`, systemType: addType, fromAssetId: addFrom || undefined, activate: true });
    } catch (e) { setError(e.message); return; }
    setAddDlg(false);
    setAddName("");
    setAddFrom("");
    setAddType("");
    navigate("/project/asset?tab=general");
  };

  const submitDelete = () => {
    try {
      removeAsset(confirmDelete.id);
      setConfirmDelete(null);
      setError("");
    } catch (e) {
      setError(e.message);
      setConfirmDelete(null);
    }
  };

  const projectName = currentProject?.name || "Untitled project";
  const meta = portfolio.project || {};

  // No width cap here: every other page fills the area beside the sidebar, and
  // this one capped itself at 1400px, which left a wide empty strip down the
  // right of a large screen. The long prose below keeps its own cap.
  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ md: "flex-end" }} spacing={1} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
            {projectName}
            {file.isDirty && (
              <Typography component="span" color="warning.main" sx={{ ml: 1, fontSize: 18, fontWeight: 700 }}>
                unsaved
              </Typography>
            )}
          </Typography>
          {meta.description ? (
            <Typography variant="body1" sx={{ mt: 0.5, maxWidth: 900 }}>
              {meta.description}
            </Typography>
          ) : null}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {isGreek
              ? `${assets.length} ${assets.length === 1 ? "μονάδα" : "μονάδες"} · καθεμία μελετάται ξεχωριστά· η συμμετοχή στις αγορές απαιτεί ξεχωριστή ενεργοποίηση και ελέγχους ετοιμότητας.`
              : `${assets.length} asset${assets.length === 1 ? "" : "s"} · each is studied on its own; market participation requires separate onboarding and readiness checks.`}
            {!currentProject?.id && (isGreek
              ? " Αποθηκεύστε το έργο (Αρχείο → Αποθήκευση ως) για να του δώσετε όνομα και να το διατηρήσετε."
              : " Save the project (File → Save As) to name it and keep it.")}
          </Typography>
        </Box>
        {/* flexShrink 0: in a new project the paragraph on the left is longer
            (it adds the "save the project" sentence), and without this the
            buttons were squeezed until "New asset" broke over two lines. */}
        <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
          {currentProject?.id && (
            <Button variant="outlined" sx={{ whiteSpace: "nowrap" }} startIcon={<EditOutlinedIcon />} onClick={() => {
              setNameDraft(currentProject.name || "");
              setRenameError("");
              setRenameOpen(true);
            }}>
              Rename project
            </Button>
          )}
          <Tooltip title={assetAdditionDisabledMessage}>
            <span>
              <Button disabled={Boolean(assetAdditionDisabledMessage)} variant="contained" sx={{ whiteSpace: "nowrap" }} startIcon={<AddOutlinedIcon />} onClick={() => { setAddFrom(""); setAddName(""); setAddType(""); setAddDlg(true); }}>
                New asset
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" onClose={() => setError("")} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid size={{ xs: 6, md: 3 }}>
          <Tile label="Assets" accent="assets" value={assets.length} hint={[
            ...ASSET_STATUSES.filter((s) => totals.byStatus[s]).map((s) => isGreek
              ? `${totals.byStatus[s]} ${({ concept: "στο στάδιο ιδέας", study: "υπό μελέτη", commissioned: "συνδεδεμένες", operating: "σε λειτουργία" })[s] || ASSET_STATUS_META[s].label.toLowerCase()}`
              : `${totals.byStatus[s]} ${ASSET_STATUS_META[s].label.toLowerCase()}`),
            ...(assets.some((a) => !ASSET_STATUSES.includes(a.status)) ? [isGreek
              ? `${assets.filter((a) => !ASSET_STATUSES.includes(a.status)).length} χρειάζονται έλεγχο`
              : `${assets.filter((a) => !ASSET_STATUSES.includes(a.status)).length} need review`] : []),
          ].join(" · ") || "—"} />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Tile label="PV installed" accent="pv" value={scaledFigure(totals.pvKw, "W", "p").value} unit={scaledFigure(totals.pvKw, "W", "p").unit} hint="sum over all assets" />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Tile
            label="Storage"
            accent="storage"
            value={scaledFigure(totals.bessMw == null ? null : totals.bessMw * 1000).value}
            unit={
              <>
                {scaledFigure(totals.bessMw == null ? null : totals.bessMw * 1000).unit}
                <Box component="span" sx={{ mx: 0.75, color: "text.disabled" }}>/</Box>
                <Box component="span" sx={{ fontSize: 20, fontWeight: 700, color: "text.primary" }}>
                  {scaledFigure(totals.bessMwh == null ? null : totals.bessMwh * 1000, "Wh").value}
                </Box>{" "}
                {scaledFigure(totals.bessMwh == null ? null : totals.bessMwh * 1000, "Wh").unit}
              </>
            }
            hint="sum of configured batteries"
          />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Tile
            label="Operating assets"
            accent="market"
            value={operating.length}
            unit={`/ ${assets.length}`}
            meter={{ value: operating.length, max: assets.length }}
            hint="Based on the selected lifecycle status; market participation is checked separately."
            muted={operationsDisabled}
            mutedNote="Market Operations is not enabled for this account."
          />
        </Grid>
      </Grid>

      <Paper variant="outlined" sx={{ borderRadius: 2, p: 2, mb: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Assets
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Select an asset from the table. The setup, data and feasibility study tabs will show its details.
            </Typography>
          </Box>
        </Stack>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Asset</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Asset type</TableCell>
              <TableCell align="right">PV</TableCell>
              <TableCell align="right">BESS</TableCell>
              <TableCell>Site</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {assets.map((a) => {
              const s = assetSummary(a.config);
              const isActive = a.id === activeAsset?.id;
              return (
                <TableRow key={a.id} hover selected={isActive} sx={{ cursor: "pointer" }} onClick={() => switchAsset(a.id)}>
                  <TableCell>
                    <Typography component="div" variant="body2" sx={{ fontWeight: 700 }}>
                      <span data-no-translate>{assetDisplayName(a.name, isGreek)}</span>
                      {isActive && assets.length > 1 && <Chip size="small" label={isGreek ? "Επιλεγμένη" : "Selected"} variant="outlined" color="primary" sx={{ ml: 1, height: 18, fontSize: 10.5, fontWeight: 700 }} />}
                    </Typography>
                    {a.notes && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", maxWidth: 360, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {a.notes}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <StatusChip operationsDisabled={operationsDisabled} isGreek={isGreek} status={a.status} onChange={(v) => updateAsset(a.id, { status: v })} />
                  </TableCell>
                  <TableCell>{s.systemType || "—"}</TableCell>
                  <TableCell align="right">{s.pvKw ? `${fmt(s.pvKw, 0)} kWp` : "—"}</TableCell>
                  <TableCell align="right">{s.bessMw ? (() => { const p = scaledFigure(s.bessMw * 1000); const e = scaledFigure(s.bessMwh * 1000, "Wh"); return `${p.value} ${p.unit} / ${e.value} ${e.unit}`; })() : "—"}</TableCell>
                  <TableCell>
                    <Typography variant="body2">{s.biddingZone || "—"}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {s.address || "—"}
                    </Typography>
                  </TableCell>
                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    <Stack direction="row" spacing={0.25} justifyContent="flex-end" alignItems="center">
                      <Button size="small" onClick={() => openSetup(a.id)}>
                        Setup
                      </Button>
                      <Button size="small" onClick={() => openStudy(a.id)}>
                        Study
                      </Button>
                      <Tooltip title="Rename / notes">
                        <IconButton size="small" onClick={() => setEditAsset({ id: a.id, name: a.name, notes: a.notes || "" })}>
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={assetAdditionDisabledMessage || "Duplicate this asset"}>
                        <span>
                        <IconButton
                          size="small"
                          aria-label={isGreek ? "Αντιγραφή μονάδας" : "Duplicate this asset"}
                          disabled={Boolean(assetAdditionDisabledMessage)}
                          onClick={() => {
                            setAddFrom(a.id);
                            {
                              const sourceType = assetTypeDefinition(a.config?.systemType)?.value || "";
                              setAddType(availableAssetTypes.some((t) => t.value === sourceType) ? sourceType : "");
                            }
                            setAddName(`${assetDisplayName(a.name, isGreek)} (${isGreek ? "αντίγραφο" : "copy"})`);
                            setAddDlg(true);
                          }}
                        >
                          <ContentCopyOutlinedIcon fontSize="small" />
                        </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={assets.length <= 1 ? "A project needs at least one asset" : "Remove asset"}>
                        <span>
                          <IconButton size="small" disabled={assets.length <= 1} onClick={() => setConfirmDelete(a)}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper variant="outlined" sx={{ borderRadius: 2, p: 2, height: "100%" }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Project details
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
              Facts that belong to the portfolio, not to a single site. Saved with the project.
            </Typography>
            <Grid container spacing={1.5}>
              <Grid size={{ xs: 12 }}>
                <ProjectReportLogo key={currentProject?.id || currentProject?.name || "new"}
                  value={meta.reportBranding} onChange={(reportBranding) => updateProjectMeta({ reportBranding })} />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  size="small"
                  fullWidth
                  multiline
                  minRows={2}
                  maxRows={4}
                  label="Project description"
                  value={meta.description || ""}
                  onChange={(e) => updateProjectMeta({ description: e.target.value })}
                  helperText="Short description used across setup, optimisation and the reports of every asset."
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField size="small" fullWidth label="Owner / client" value={meta.owner || ""} onChange={(e) => updateProjectMeta({ owner: e.target.value })} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField size="small" fullWidth label="Company / SPV" value={meta.company || ""} onChange={(e) => updateProjectMeta({ company: e.target.value })} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl size="small" fullWidth>
                  <InputLabel id="pm-access">Market access</InputLabel>
                  <Select labelId="pm-access" label="Market access" value={meta.accessRoute || ""} onChange={(e) => updateProjectMeta({ accessRoute: e.target.value })}>
                    <MenuItem value="">Not decided</MenuItem>
                    <MenuItem value="direct">Direct participant (own BRP)</MenuItem>
                    <MenuItem value="aggregator">Via aggregator</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  size="small"
                  fullWidth
                  label="Aggregator / BRP"
                  value={meta.aggregatorName || ""}
                  onChange={(e) => updateProjectMeta({ aggregatorName: e.target.value })}
                  disabled={!meta.accessRoute}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField size="small" fullWidth multiline minRows={2} label="Notes" value={meta.notes || ""} onChange={(e) => updateProjectMeta({ notes: e.target.value })} />
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper variant="outlined" sx={{ borderRadius: 2, p: 2, height: "100%" }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              How an asset moves through the platform
            </Typography>
            <Stack spacing={1} sx={{ mt: 1.5 }}>
              {ASSET_STATUSES.map((s) => (
                <Stack key={s} direction="row" spacing={1.5} alignItems="flex-start">
                  <Chip size="small" label={ASSET_STATUS_META[s].label} color={ASSET_STATUS_META[s].color} sx={{ minWidth: 118, fontWeight: 700 }} />
                  <Typography variant="body2" color="text.secondary">
                    {ASSET_STATUS_META[s].hint}
                    {s === "study" && " Asset Setup and the Feasibility Study work on one asset at a time."}
                    {s === "operating" && " Appears in Operations → Portfolio for onboarding (market access, metering, prequalification)."}
                  </Typography>
                </Stack>
              ))}
            </Stack>
            <Divider sx={{ my: 1.5 }} />
            <Typography variant="caption" color="text.secondary">
              The active asset's configuration is what the backend runs today (Daily Optimization, feasibility simulation). The other assets are stored with the project and swapped in when you switch.
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      <Dialog open={renameOpen} onClose={() => !renaming && setRenameOpen(false)} fullWidth maxWidth="xs">
        <Box component="form" onSubmit={submitRename}>
          <DialogTitle>Rename project</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              required
              fullWidth
              label="Project name"
              value={nameDraft}
              onChange={(event) => setNameDraft(event.target.value)}
              disabled={renaming}
              sx={{ mt: 1 }}
            />
            {renameError && <Alert severity="error" sx={{ mt: 2 }}>{renameError}</Alert>}
          </DialogContent>
          <DialogActions>
            <Button disabled={renaming} onClick={() => setRenameOpen(false)}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={renaming || !nameDraft.trim() || nameDraft.trim() === currentProject?.name}>
              {renaming ? "Saving…" : "Save"}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      {/* New / duplicate asset */}
      <Dialog open={addDlg} onClose={() => setAddDlg(false)} fullWidth maxWidth="xs">
        <DialogTitle>{addFrom ? "Duplicate asset" : "New asset"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <TextField autoFocus label="Asset name" size="small" value={addName} onChange={(e) => setAddName(e.target.value)} placeholder={`${isGreek ? "Μονάδα" : "Asset"} ${assets.length + 1}`} />
            <FormControl size="small" required>
              <InputLabel id="add-type">Asset type</InputLabel>
              <Select labelId="add-type" label="Asset type" value={addType} onChange={(e) => setAddType(e.target.value)}>
                {ASSET_TYPES.map((type) => (
                  <MenuItem
                    key={type.value}
                    value={type.value}
                    disabled={!availableAssetTypes.some((available) => available.value === type.value)}
                  >
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {windDisabledMessage ? (
              <Typography variant="caption" color="text.secondary">
                Wind asset types are shown for reference and require Wind System access.
              </Typography>
            ) : null}
            <Typography variant="caption" color="text.secondary">PV and wind belong to separate assets. Copies include only equipment applicable to the selected type.</Typography>
            <FormControl size="small">
              <InputLabel id="add-from">Start from</InputLabel>
              <Select labelId="add-from" label="Start from" value={addFrom} onChange={(e) => setAddFrom(e.target.value)}>
                <MenuItem value="">Platform defaults</MenuItem>
                {assets.map((a) => (
                  <MenuItem key={a.id} value={a.id}>
                    {isGreek ? "Αντιγραφή ρυθμίσεων από" : "Copy settings from"} <span data-no-translate>{assetDisplayName(a.name, isGreek)}</span>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDlg(false)}>Cancel</Button>
          <Button variant="contained" onClick={submitAdd} disabled={!addType || Boolean(assetAdditionDisabledMessage)}>
            Create and open
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit asset */}
      <Dialog open={Boolean(editAsset)} onClose={() => setEditAsset(null)} fullWidth maxWidth="xs">
        <DialogTitle>Edit asset</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <TextField autoFocus label="Asset name" size="small" value={editAsset?.name || ""} onChange={(e) => setEditAsset((v) => ({ ...v, name: e.target.value }))} />
            <TextField label="Notes" size="small" multiline minRows={2} value={editAsset?.notes || ""} onChange={(e) => setEditAsset((v) => ({ ...v, notes: e.target.value }))} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditAsset(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              updateAsset(editAsset.id, { name: editAsset.name.trim() || (isGreek ? "Μονάδα" : "Asset"), notes: editAsset.notes });
              setEditAsset(null);
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete */}
      <Dialog open={Boolean(confirmDelete)} onClose={() => setConfirmDelete(null)} maxWidth="xs">
        <DialogTitle>{isGreek ? "Αφαίρεση μονάδας" : "Remove asset"}: <span data-no-translate>{assetDisplayName(confirmDelete?.name, isGreek)}</span>?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            The asset's configuration and study assumptions are removed from this project when you next save. This cannot be undone after saving.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={submitDelete}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
